// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import styles from './index.module.scss';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { InfoBlock } from 'components/molecules/MyPage/InfoBlock';
import { Field } from 'components/molecules/MyPage/Field';
import { EmailListField } from 'components/molecules/MyPage/EmailListField';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { MyPageType } from 'types/mypage';
import { useMypageContactInfo } from 'util/hooks/useMypageContactInfo';
import { REGEX_OBJECT } from 'util/ConstantValues';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';

export const EmissionAdminSite = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const hasValidUser = !!user.userId && !!user.role;

  const myPage = useMypageContactInfo<MyPageType.EmissionAdminSite>({
    myPageType: MyPageType.EmissionAdminSite,
    enabled: hasValidUser,
  });

  return (
    <div className={styles.mypage}>
      <div className={styles.generalInfo}>
        <InfoBlock
          className={styles.accountInfo}
          title={t('my_page.account_info.title')}
        >
          <div className={styles.col}>
            <Field
              label={t('my_page.account_info.account_category')}
              value={t('my_page.emission_admin_operator.emission')}
            />
            <Field
              label={t('my_page.account_info.account_permissions')}
              value={t('my_page.emission_admin_site.admin_site')}
            />
          </div>
        </InfoBlock>
        <InfoBlock
          className={styles.masterInfo}
          title={t('my_page.emission_admin_site.master_info.title')}
        >
          <div className={styles.grid}>
            <Field
              label={t('my_page.emission_admin_site.master_info.company_name')}
              value={myPage.data?.operatorName ?? ''}
            />
            <Field
              label={t('my_page.emission_admin_site.master_info.site_name')}
              value={myPage.data?.siteName ?? ''}
            />
            <Field
              label={t('my_page.emission_admin_site.master_info.site_address')}
              value={myPage.data?.address ?? ''}
            />
            <Field
              label={t(
                'my_page.emission_admin_site.master_info.contact_person_name'
              )}
              value={myPage.data?.staffName ?? ''}
              format="notSpecialCharacter"
              required
              maxLength={100}
              onEdit={(value) =>
                myPage.handleChangeInfo({
                  staffName: value,
                  contactEmails: myPage.data?.contactEmails,
                  tel: myPage.data?.tel,
                  invoiceEmails: myPage.data?.invoiceEmails,
                })
              }
            />
            <EmailListField
              label={t('my_page.emission_admin_site.master_info.email')}
              extraLabel={
                <Popover
                  content={t(
                    'my_page.emission_admin_site.master_info.email_tooltip'
                  )}
                  positions={['top']}
                  containerStyle={{
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <IconInfo fill="#878787" />
                  </span>
                </Popover>
              }
              values={myPage.data?.contactEmails ?? []}
              placeholder={
                t('my_page.emission_admin_site.master_info.email') || ''
              }
              required
              onEdit={(emails) =>
                myPage.handleChangeInfo({
                  staffName: myPage.data?.staffName,
                  contactEmails: emails,
                  tel: myPage.data?.tel,
                  invoiceEmails: myPage.data?.invoiceEmails,
                })
              }
            />
            <Field
              label={t('my_page.emission_admin_site.master_info.phone_number')}
              value={myPage.data?.tel ?? ''}
              format="phone"
              regex={REGEX_OBJECT.number}
              required
              maxLength={15}
              onEdit={(value) =>
                myPage.handleChangeInfo({
                  staffName: myPage.data?.staffName,
                  contactEmails: myPage.data?.contactEmails,
                  tel: value,
                  invoiceEmails: myPage.data?.invoiceEmails,
                })
              }
            />
          </div>

          <hr />

          <EmailListField
            label={t('my_page.emission_admin_site.master_info.invoicing_email')}
            values={myPage.data?.invoiceEmails ?? []}
            placeholder={
              t('my_page.emission_admin_site.master_info.invoicing_email') || ''
            }
            onEdit={(emails) =>
              myPage.handleChangeInfo({
                staffName: myPage.data?.staffName,
                contactEmails: myPage.data?.contactEmails,
                tel: myPage.data?.tel,
                invoiceEmails: emails,
              })
            }
          />
        </InfoBlock>
      </div>
      <div className={styles.additionalInfo}>
        <InfoBlock title={t('my_page.emission_admin_site.list.title')}>
          <div className={styles.tableWrapper}>
            <table
              className={styles.table}
              style={{
                minWidth: 1000,
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>
                    {t('my_page.emission_admin_site.list.item_name')}
                  </th>
                  <th>
                    {t('my_page.emission_admin_site.list.processing_method')}
                  </th>
                  <th>
                    {t('my_page.emission_admin_site.list.collection_company')}
                  </th>
                  <th className={styles.right}>
                    {t('my_page.emission_admin_site.list.unit_price')}
                  </th>
                  <th className={styles.center}>
                    {t('my_page.emission_admin_site.list.co2_emission_factor')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {myPage.data?.items?.length ? (
                  myPage.data.items.map((item, index) => {
                    return (
                      <tr key={index}>
                        <td
                          className={mergeClasses(
                            styles.light,
                            styles.bold,
                            styles.large
                          )}
                        >
                          <p>{item.name}</p>
                        </td>
                        <td>
                          <p>{item.recycleMethodName}</p>
                        </td>
                        <td>
                          <p>{item.collectionSite}</p>
                        </td>
                        <td
                          className={mergeClasses(styles.right, styles.light)}
                        >
                          <p>
                            {item.price != null && item.price !== ''
                              ? t(
                                  'my_page.emission_admin_operator.list.price_with_unit',
                                  {
                                    price: normalizeNumber({
                                      value: item.price,
                                    }),
                                  }
                                )
                              : t(
                                  'my_page.emission_admin_operator.list.price_with_unit',
                                  {
                                    price: 0,
                                  }
                                )}
                          </p>
                        </td>
                        <td className={styles.center}>
                          <p>
                            {normalizeNumber({
                              value: item.co2Factor,
                              toFixed: 3,
                            })}
                          </p>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className={styles.noData}>
                      {t('messages.M_008')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfoBlock>
      </div>
    </div>
  );
};
